/**
 * The password policy, mirrored from the server so a form never enables its submit
 * button for something the API will reject.
 *
 * Kept in one place because it was previously re-implemented in three screens, and all
 * three omitted the blocked-fragment check — so "Tejas@12345678" passed every client
 * rule and then failed on the server with a message naming fragments it did not
 * contain. Keep this in step with `apps/api/src/auth/password.service.ts`.
 */

/** Mirrors MIN_PASSWORD_LENGTH in apps/api/src/auth/password.service.ts. */
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 200;

/** Mirrors WEAK_FRAGMENTS in password.service.ts. */
const WEAK_FRAGMENTS = [
  'password',
  'ambition',
  'welcome',
  'qwerty',
  'letmein',
  '123456',
  'abcdef',
];

export function findWeakFragment(password: string): string | null {
  const lowered = password.toLowerCase();
  return WEAK_FRAGMENTS.find((fragment) => lowered.includes(fragment)) ?? null;
}

export interface PasswordCheckOptions {
  /** Compared so "your new password must differ from the current one" is caught early. */
  currentPassword?: string;
  /** When present, both must match. */
  confirmation?: string;
}

/**
 * Returns human-readable problems, empty when acceptable. Nothing is reported while the
 * field is still empty — telling someone their password is too short before they have
 * typed anything is noise.
 */
export function passwordProblems(
  password: string,
  { currentPassword, confirmation }: PasswordCheckOptions = {},
): string[] {
  const problems: string[] = [];
  if (password.length === 0) {
    return confirmation && confirmation.length > 0 ? ['Both passwords must match'] : [];
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    problems.push(`At least ${MIN_PASSWORD_LENGTH} characters`);
  }
  if (password.length > MAX_PASSWORD_LENGTH) {
    problems.push(`No more than ${MAX_PASSWORD_LENGTH} characters`);
  }
  if (!/[a-z]/i.test(password)) problems.push('At least one letter');
  if (!/\d/.test(password)) problems.push('At least one number');
  if (/^\s|\s$/.test(password)) problems.push('No leading or trailing spaces');

  const weak = findWeakFragment(password);
  if (weak) {
    // Naming the fragment is the whole point — "contains a common word" leaves the
    // person guessing which part of their password is the problem.
    problems.push(`Must not contain “${weak}”`);
  }

  if (currentPassword !== undefined && password === currentPassword) {
    problems.push('Must be different from your current password');
  }
  if (confirmation !== undefined && confirmation.length > 0 && password !== confirmation) {
    problems.push('Both passwords must match');
  }

  return problems;
}

/** True when the password satisfies every rule and matches its confirmation. */
export function isPasswordAcceptable(
  password: string,
  options: PasswordCheckOptions = {},
): boolean {
  if (password.length < MIN_PASSWORD_LENGTH) return false;
  if (options.confirmation !== undefined && password !== options.confirmation) return false;
  return passwordProblems(password, options).length === 0;
}
